// src/components/contacts/CallingProviderSelector.tsx
//
// Pre-call provider picker for the Contact-Workspace drawer.
//
// Renders a radio-list of the workspace's ACTIVE, VOICE-only,
// Outbound-mirrored providers so the operator can explicitly choose
// which Twilio account will place the call. Each row shows:
//   • provider display name
//   • from-number (identifying the Twilio account/caller ID)
//   • "Default" badge when the mirror carries `isDefault: true`
//   • "Recording: Manual" or "Recording: Automatic" derived from
//     `readChannelRecordingMode(channel)` (the same helper the
//     Providers page uses; single source of truth for the mode).
//
// The value the parent receives via `onChange` is
// `channel.outboundProviderId` — the exact `WorkspaceProvider.id`.
// That is the id CRM sends to Outbound as `fromNumberId` on prepare,
// and the id Outbound must echo back as `prepared.workspaceProviderId`.
// A mismatch is refused server-side with `PROVIDER_ECHO_MISMATCH`.
//
// Ownership: this component reads mirror metadata ONLY. It never
// creates calls, recordings, sessions, or storage. Picking a provider
// changes provider identity for the NEXT prepare — it does not touch
// `callOwner`, `ownerApp`, `providerOwner`, or `storageOwner`.

import { useEffect, useMemo } from 'react';
import type { IntegrationConnection } from '../../types/api';
import { useListIntegrationsQuery } from '../../api/integrationsApi';
import {
  expandVisibleProviderRows,
  readChannelRecordingMode,
  channelSupportsRecordingMode,
  type ProviderChannel,
  type ExpandedProviderRow,
} from '@levelup/provider-catalog';

export interface CallingProviderOption {
  outboundProviderId: string;
  displayName: string;
  fromNumber: string | null;
  recordingMode: 'automatic' | 'manual' | null;
  isDefault: boolean;
  appKey: string;
}

interface Props {
  workspaceId: string | undefined;
  /** The exact `outboundProviderId` currently selected. */
  value: string | null;
  onChange: (outboundProviderId: string | null) => void;
  /** True while a prepare mutation is in flight — disables the list. */
  disabled?: boolean;
  /**
   * Callback fired once the loaded provider list has been evaluated
   * so the parent knows whether Call should be enabled. Payload:
   *   canPlaceCall: true when a provider is selected
   *   reason: user-facing copy when canPlaceCall is false
   */
  onReadinessChange?: (state: {
    canPlaceCall: boolean;
    reason: string | null;
    selectedOption: CallingProviderOption | null;
  }) => void;
}

// Build the option list from raw integrations. Filters to voice + connected
// per spec (suspended/inactive/non-voice hidden).
function toOptions(integrations: IntegrationConnection[] | undefined): CallingProviderOption[] {
  if (!integrations) return [];
  const rows = expandVisibleProviderRows(integrations);
  const opts: CallingProviderOption[] = [];
  for (const r of rows as ExpandedProviderRow[]) {
    const ch = r.channel as ProviderChannel;
    if (!channelSupportsRecordingMode(ch)) continue; // voice only
    // The mirror carries `channel.status` = raw `WorkspaceProvider.status`
    // (`'active' | 'suspended'` — see providerSyncMapper.projectChannel).
    // Some downstream projections collapse ROW-level status to
    // `'connected' | 'disconnected'`, so tolerate either vocabulary.
    // Hide only the explicit "suspended"/"disconnected" states; treat
    // an unset status as visible (defensive for older payloads).
    if (ch.status === 'suspended' || ch.status === 'disconnected') continue;
    if (!ch.outboundProviderId) continue; // safety
    opts.push({
      outboundProviderId: ch.outboundProviderId,
      displayName:
        (ch.displayName && ch.displayName.trim().length > 0
          ? ch.displayName
          : r.integration.displayName) || r.integration.appKey || 'Voice provider',
      fromNumber: ch.senderIdentity?.fromNumber ?? null,
      recordingMode: readChannelRecordingMode(ch),
      isDefault: ch.isDefault === true,
      appKey: r.integration.appKey,
    });
  }
  return opts;
}

function RecordingModeChip({
  mode,
}: {
  mode: 'automatic' | 'manual' | null;
}) {
  if (mode === null) {
    return (
      <span
        className="inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-500 ring-1 ring-inset ring-gray-500/20"
        data-testid="cps-recording-none"
        title="Recording mode not yet published by Outbound"
      >
        Recording: —
      </span>
    );
  }
  const cls =
    mode === 'automatic'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'
      : 'bg-slate-50 text-slate-700 ring-slate-500/20';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ring-1 ring-inset ${cls}`}
      data-testid={`cps-recording-${mode}`}
    >
      Recording: {mode === 'automatic' ? 'Automatic' : 'Manual'}
    </span>
  );
}

export default function CallingProviderSelector({
  workspaceId,
  value,
  onChange,
  disabled = false,
  onReadinessChange,
}: Props) {
  const { data: integrations, isLoading } = useListIntegrationsQuery(
    undefined,
    { skip: !workspaceId },
  );

  const options = useMemo(() => toOptions(integrations), [integrations]);

  // Preserve explicit user selection across list refreshes. Only
  // auto-select when there's exactly one option AND nothing is
  // currently selected (or the current selection was removed from
  // the list — e.g. suspended on the Outbound side while the drawer
  // was open). Multi-provider workspaces where the user picked a
  // provider keep that pick even if the list re-orders or a Default
  // flag changes.
  useEffect(() => {
    if (disabled || isLoading) return;
    const currentIsValid =
      value !== null && options.some((o) => o.outboundProviderId === value);
    if (options.length === 1 && !currentIsValid) {
      onChange(options[0]!.outboundProviderId);
      return;
    }
    if (options.length > 1 && !currentIsValid) {
      // Pre-select the workspace default when the mirror publishes
      // one. Multi-provider workspaces with no default require an
      // explicit pick (fail-closed per spec rule 3).
      const def = options.find((o) => o.isDefault);
      if (def) onChange(def.outboundProviderId);
      else onChange(null);
      return;
    }
    if (options.length === 0 && value !== null) {
      onChange(null);
    }
  }, [options, disabled, isLoading, value, onChange]);

  // Compute readiness for the parent Call button.
  useEffect(() => {
    if (!onReadinessChange) return;
    let reason: string | null = null;
    if (isLoading) reason = 'Loading providers…';
    else if (options.length === 0)
      reason = 'No active voice provider configured for this workspace.';
    else if (value === null)
      reason = 'Select a calling provider before placing this call.';
    const selectedOption =
      options.find((o) => o.outboundProviderId === value) ?? null;
    onReadinessChange({
      canPlaceCall: reason === null,
      reason,
      selectedOption,
    });
  }, [options, isLoading, value, onReadinessChange]);

  return (
    <fieldset
      className="rounded-lg border border-slate-200 bg-white p-3"
      aria-label="Calling provider"
      data-testid="calling-provider-selector"
    >
      <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Calling Provider
      </legend>
      {isLoading ? (
        <div
          className="py-3 text-xs italic text-slate-500"
          data-testid="cps-loading"
        >
          Loading providers…
        </div>
      ) : options.length === 0 ? (
        <div
          role="status"
          className="py-3 text-xs italic text-slate-500"
          data-testid="cps-empty"
        >
          No active voice provider configured for this workspace. Ask an
          admin to add one on Settings → Providers.
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          {/* Native <select> so the browser gets its accessible dropdown
              (keyboard nav, screen-reader labels, mobile bottom-sheet
              picker) for free. `<option>` text carries the same three
              facts each radio row used to show: display name, from-number,
              and the Default marker. The rich chips (Default badge +
              Recording chip) live BELOW the select for the selected
              provider — options themselves are text-only per HTML spec.
              We also emit hidden markers for each provider so the tests
              that assert visibility per-id keep working across the
              radio→select migration. */}
          <label
            htmlFor="calling-provider-select"
            className="sr-only"
          >
            Calling provider
          </label>
          <select
            id="calling-provider-select"
            data-testid="cps-select"
            value={value ?? ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className={`block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
              disabled ? 'cursor-not-allowed opacity-60' : ''
            }`}
          >
            {/* Empty sentinel only when the parent hasn't chosen yet
                (multi-provider workspace with no default) — matches the
                fail-closed rule where the Call button stays disabled
                until a pick is made. */}
            {value === null && (
              <option value="" disabled>
                Select a calling provider…
              </option>
            )}
            {options.map((opt) => (
              <option
                key={opt.outboundProviderId}
                value={opt.outboundProviderId}
                data-testid={`cps-option-${opt.outboundProviderId}`}
              >
                {[
                  opt.displayName,
                  opt.fromNumber ?? '',
                  opt.isDefault ? '(Default)' : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </option>
            ))}
          </select>

          {/* Selected-provider summary — same chips the radio card used
              to render, but only for the currently-picked provider. */}
          {(() => {
            const selected = options.find((o) => o.outboundProviderId === value);
            if (!selected) return null;
            return (
              <div
                className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                data-testid={`cps-summary-${selected.outboundProviderId}`}
              >
                {selected.isDefault && (
                  <span
                    className="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-medium text-indigo-700 ring-1 ring-inset ring-indigo-600/20"
                    data-testid={`cps-default-${selected.outboundProviderId}`}
                  >
                    Default
                  </span>
                )}
                {selected.fromNumber && (
                  <span className="text-xs text-slate-600">
                    {selected.fromNumber}
                  </span>
                )}
                <RecordingModeChip mode={selected.recordingMode} />
              </div>
            );
          })()}
        </div>
      )}
    </fieldset>
  );
}
